import { cn } from "~/lib/utils"
import { Checkbox } from "~/react/ui/checkbox"
import { Button } from "~/react/ui/button"

export type DataTableColumn<T = unknown> = {
  name: string
  label: React.ReactNode
  accessor?: (row: T) => React.ReactNode
}

type DataTableProps = {
  className?: string
  children: React.ReactNode
  bulkActionsBar?: React.ReactNode
}

export function DataTable({ className, children, bulkActionsBar }: DataTableProps) {
  return (
    <div className={cn("h-data-table h-data-table--full", className)}>
      {bulkActionsBar}
      <table className="table mb-0">{children}</table>
    </div>
  )
}

type DataTableHeaderProps = {
  children: React.ReactNode
}

export function DataTableHeader({ children }: DataTableHeaderProps) {
  return (
    <thead className="h-data-table__heading">
      <tr>{children}</tr>
    </thead>
  )
}

type DataTableHeaderCellProps = React.ThHTMLAttributes<HTMLTableCellElement> & {
  children?: React.ReactNode
  sticky?: boolean
}

export function DataTableHeaderCell({ children = null, className, sticky, ...rest }: DataTableHeaderCellProps) {
  const isCheckboxCell = className?.includes("h-data-table__checkbox")

  return (
    <th className={cn("h-data-table__th", className)} {...rest}>
      {isCheckboxCell ? (
        children
      ) : (
        <div className={cn("h-data-table-cell h-data-table-cell--heading", sticky && "h-data-table-cell--sticky")}>
          {children}
        </div>
      )}
    </th>
  )
}

type DataTableBodyProps = {
  children: React.ReactNode
}

export function DataTableBody({ children }: DataTableBodyProps) {
  return <tbody>{children}</tbody>
}

type DataTableBodySectionProps = {
  children: React.ReactNode
}

export function DataTableBodySection({ children }: DataTableBodySectionProps) {
  return <>{children}</>
}

type DataTableGroupRowProps = {
  checkboxCell?: React.ReactNode
  children?: React.ReactNode
  colSpan?: number
  sectionId?: string
}

export function DataTableGroupRow({
  checkboxCell = null,
  children = null,
  colSpan = 4,
  sectionId,
}: DataTableGroupRowProps) {
  return (
    <tr className="h-data-table__row-group" data-section={sectionId}>
      <td className="h-data-table__checkbox">
        <div className="h-data-table-cell h-data-table-cell--checkbox">
          {checkboxCell !== null ? checkboxCell : <Checkbox aria-label={i18n.t("js.data_table.select_group")} />}
        </div>
      </td>
      <td colSpan={colSpan}>
        <div className="h-data-table-cell">{children}</div>
      </td>
    </tr>
  )
}

type DataTableBodySectionHeaderProps = {
  children: React.ReactNode
  className?: string
}

export function DataTableBodySectionHeader({ children, className }: DataTableBodySectionHeaderProps) {
  return (
    <tr className={cn("h-data-table-row h-data-table-row--group", className)}>
      <td className="h-data-table__checkbox">
        <div className="h-data-table-cell h-data-table-cell--checkbox">
          <Checkbox aria-label={i18n.t("js.data_table.select_group")} />
        </div>
      </td>
      <td colSpan={999}>
        <div className="h-data-table-cell">{children}</div>
      </td>
    </tr>
  )
}

type DataTableBodySectionContentProps = {
  children: React.ReactNode
}

export function DataTableBodySectionContent({ children }: DataTableBodySectionContentProps) {
  return <>{children}</>
}

type DataTableRowProps = {
  children: React.ReactNode
  className?: string
  id?: string | number
}

export function DataTableRow({ children, className, id }: DataTableRowProps) {
  return (
    <tr className={cn("h-data-table-row", className)} data-id={id}>
      {children}
    </tr>
  )
}

type DataTableCellProps = {
  children?: React.ReactNode
  className?: string
  sticky?: boolean
}

export function DataTableCell({ children = null, className, sticky }: DataTableCellProps) {
  return (
    <td>
      <div className={cn("h-data-table-cell", sticky && "h-data-table-cell--sticky", className)}>{children}</div>
    </td>
  )
}

type DataTableCellActionsProps = {
  children?: React.ReactNode
  className?: string
  alwaysVisible?: boolean
}

export function DataTableCellActions({ children = null, alwaysVisible = false, className }: DataTableCellActionsProps) {
  return <div className={cn("h-data-table-cell__actions", className, alwaysVisible && "is-visible")}>{children}</div>
}

type DataTableCheckboxCellProps = {
  checked?: boolean
  disabled?: boolean
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  "aria-label"?: string
  recordId?: string | number
  section?: string
}

export function DataTableCheckboxCell({
  checked = false,
  disabled = false,
  "aria-label": ariaLabel = i18n.t("js.data_table.select_record"),
  recordId,
  section,
  onChange,
}: DataTableCheckboxCellProps) {
  return (
    <td className="h-data-table__checkbox">
      <div className="h-data-table-cell h-data-table-cell--checkbox">
        <Checkbox
          checked={checked}
          disabled={disabled}
          onChange={onChange}
          aria-label={ariaLabel}
          data-record-id={recordId}
          data-section={section}
        />
      </div>
    </td>
  )
}

type DataTableCheckboxPlaceholderCellProps = {
  "aria-label"?: string
}

export function DataTableCheckboxPlaceholderCell({
  "aria-label": ariaLabel = i18n.t("js.data_table.select_record"),
}: DataTableCheckboxPlaceholderCellProps) {
  return (
    <td className="h-data-table__checkbox">
      <div className="h-data-table-cell h-data-table-cell--checkbox" aria-label={ariaLabel} />
    </td>
  )
}

type DataTableEmptyStateProps = {
  colSpan: number
  title?: string
  description?: string
  onClearFilters?: () => void
  clearFiltersLabel?: string
}

export function DataTableEmptyState({
  colSpan,
  title = i18n.t("js.data_table.no_results.title"),
  description = i18n.t("js.data_table.no_results.description"),
  onClearFilters,
  clearFiltersLabel = i18n.t("js.data_table.no_results.cta"),
}: DataTableEmptyStateProps) {
  return (
    <tr className="h-data-table-row h-data-table-row--empty">
      <td colSpan={colSpan}>
        <div className="empty-state empty-state--small">
          <div className="empty-state__content">
            <h3 className="empty-state__title text-sm-medium">{title}</h3>
            <p className="empty-state__description">{description}</p>
            <div className="empty-state__actions">
              {onClearFilters && (
                <Button size="sm" onClick={onClearFilters}>
                  {clearFiltersLabel}
                </Button>
              )}
            </div>
          </div>
        </div>
      </td>
    </tr>
  )
}
